import React from "react";
import { useRef, useState } from "react";

import { toast } from "./lib/toast";
import {
  FileStack,
  FileText,
  X,
  Check,
  ChevronLeft,
  ChevronRight,
  Sparkles,
} from "./icons";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  Button,
  Input,
} from "./ui";
import { ClassificationLink } from "./ClassificationLink";
import {
  addImportProducts,
  addImportEntry,
  newProductId,
  newEntryId,
} from "./lib/import-data";
import type { ImportProduct } from "./lib/import-types";


type Step = "upload" | "parsing" | "review" | "done";

interface ExtractedRow {
  name: string;
  sku: string;
  htsCode: string;
  countryOfOrigin: string;
  declaredValue: number;
  source: string;
}

const MOCK_EXTRACT: ExtractedRow[] = [
  { name: "Catalytic converter assembly", sku: "AUT-5005", htsCode: "8708.92.50", countryOfOrigin: "Mexico", declaredValue: 18500, source: "Commercial Invoice" },
  { name: "Heat shield bracket", sku: "AUT-5005-A", htsCode: "8708.92.75", countryOfOrigin: "Mexico", declaredValue: 1200, source: "Packing List" },
  { name: "Mounting hardware kit", sku: "AUT-5005-B", htsCode: "7318.15.80", countryOfOrigin: "Taiwan", declaredValue: 480, source: "Commercial Invoice" },
];

export const EntryPacketUploadDialog: React.FC<{
  open: boolean;
  onOpenChange: (v: boolean) => void;
  databaseId: string;
}> = ({
  open,
  onOpenChange,
  databaseId,
}) => {
  const [step, setStep] = useState<Step>("upload");
  const [files, setFiles] = useState<File[]>([]);
  const [rows, setRows] = useState<ExtractedRow[]>([]);
  const [entryNumber, setEntryNumber] = useState("");
  const [port, setPort] = useState("Los Angeles/Long Beach, CA");
  const inputRef = useRef<HTMLInputElement>(null);

  const reset = () => {
    setStep("upload");
    setFiles([]);
    setRows([]);
    setEntryNumber("");
  };
  const close = () => { onOpenChange(false); setTimeout(reset, 200); };

  const addFiles = (list: FileList | null) => {
    if (!list) return;
    setFiles((prev) => [...prev, ...Array.from(list)]);
  };

  const parse = () => {
    setStep("parsing");
    setTimeout(() => {
      setRows(MOCK_EXTRACT);
      setEntryNumber(`E2025-${String(Math.floor(Math.random() * 9000) + 1000)}`);
      setStep("review");
    }, 1400);
  };

  const confirm = () => {
    const productIds: string[] = [];
    const products: ImportProduct[] = rows.map((r, i) => {
      // newProductId() is derived from IMPORT_PRODUCTS.length, which doesn't change
      // until addImportProducts() runs below — every row in this .map() would
      // otherwise get the same id. Suffix with the row index to keep them unique.
      const id = `${newProductId()}-${i}`;
      productIds.push(id);
      return {
        id, sku: r.sku, name: r.name,
        description: `${r.name} — extracted from entry packet.`,
        htsCode: r.htsCode, countryOfOrigin: r.countryOfOrigin,
        supplierId: "ie-13", manufacturerId: "ie-13",
        declaredValue: r.declaredValue, unitOfMeasure: "Each",
        importDatabaseId: databaseId, complianceScore: 80, riskLevel: "Low",
        pgaFlags: [], section301: false, section301Rate: 0, section232: false, section232Rate: 0,
        additionalDutyPct: 0, fta: "none", ftaEligible: false, uflpaRisk: "none",
      };
    });
    addImportProducts(products);
    if (productIds[0]) {
      addImportEntry({
        id: newEntryId(),
        entryNumber,
        productId: productIds[0],
        importDatabaseId: databaseId,
        entryDate: new Date().toISOString(),
        portOfEntry: port,
        value: rows.reduce((s, r) => s + r.declaredValue, 0),
        status: "Pending",
        countryOfOrigin: rows[0].countryOfOrigin,
        htsCode: rows[0].htsCode,
        documents: files.map((file, index) => ({
          id: `doc-upload-${index}`,
          name: file.name,
          type: index === 0 ? "Commercial Invoice" : index === 1 ? "Packing List" : "Entry Summary / 7501",
          uploadedAt: new Date().toISOString(),
          size: `${Math.max(1, Math.round(file.size / 1024))} KB`,
        })),
      });
    }
    setStep("done");
    toast.success(`Created ${products.length} products + entry ${entryNumber}`);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); }}>
      <DialogContent size="lg" onClose={close}>
        <DialogHeader>
          <DialogTitle>Upload entry packet</DialogTitle>
        </DialogHeader>
        <DialogBody>
          {step === "upload" && (
            <div className="ih-col ih-gap-3">
              <div
                role="button"
                tabIndex={0}
                onClick={() => inputRef.current?.click()}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") inputRef.current?.click(); }}
                className="ih-dropzone"
              >
                <FileStack size={32} className="ih-text-muted" />
                <div className="ih-mt-2 ih-font-medium ih-text-sm">Click to upload PDFs</div>
                <div className="ih-text-xs ih-text-muted">
                  Commercial invoice, packing list, entry summary, PGA filings…
                </div>
                <input
                  ref={inputRef}
                  type="file"
                  multiple
                  accept=".pdf"
                  className="ih-hidden"
                  onChange={(e) => addFiles(e.target.files)}
                />
              </div>
              {files.length > 0 && (
                <ul className="ih-file-list">
                  {files.map((f, i) => (
                    <li key={i} className="ih-file-list-item">
                      <FileText size={14} className="ih-text-muted" />
                      <span className="ih-grow ih-truncate">{f.name}</span>
                      <span className="ih-text-muted ih-num">
                        {Math.round(f.size / 1024)} KB
                      </span>
                      <button
                        type="button"
                        aria-label={`Remove ${f.name}`}
                        className="ih-icon-btn"
                        onClick={() => setFiles((p) => p.filter((_, j) => j !== i))}
                      >
                        <X size={12} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {step === "parsing" && (
            <div className="ih-progress-block">
              <Sparkles size={32} className="ih-tone-info ih-pulse" />
              <div className="ih-font-medium ih-text-sm">Parsing packet…</div>
              <div className="ih-text-xs ih-text-muted">
                Extracting line items from {files.length} document{files.length > 1 ? "s" : ""}
              </div>
            </div>
          )}

          {step === "review" && (
            <div className="ih-col ih-gap-3">
              <div className="ih-form-grid-2">
                <Field label="Entry number" value={entryNumber} onChange={setEntryNumber} />
                <Field label="Port of entry" value={port} onChange={setPort} />
              </div>
              <div className="ih-kicker">Extracted products</div>
              <div className="ih-classify-callout">
                <div>
                  <div className="ih-text-xs ih-font-medium">Missing or uncertain classification?</div>
                  <div className="ih-text-xs ih-text-muted">
                    Send a line item to BITE Classify and return the approved HTS code here.
                  </div>
                </div>
                <ClassificationLink hub="import" compact />
              </div>
              <div className="ih-subsurface" style={{ overflowX: "auto", padding: 4 }}>
                <table className="ih-text-xs" style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr className="ih-text-muted">
                      <th className="ih-preview-th">Name</th>
                      <th className="ih-preview-th">SKU</th>
                      <th className="ih-preview-th">HTS</th>
                      <th className="ih-preview-th">COO</th>
                      <th className="ih-preview-th ih-text-right">Value</th>
                      <th className="ih-preview-th">Source</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={i} className="ih-review-row-tr">
                        <td className="ih-review-cell"><Input value={r.name} onChange={(e) => upd(setRows, i, { name: e.target.value })} className="ih-input-sm" /></td>
                        <td className="ih-review-cell"><Input value={r.sku} onChange={(e) => upd(setRows, i, { sku: e.target.value })} className="ih-input-sm ih-mono" /></td>
                        <td className="ih-review-cell"><Input value={r.htsCode} onChange={(e) => upd(setRows, i, { htsCode: e.target.value })} className="ih-input-sm ih-mono" /></td>
                        <td className="ih-review-cell"><Input value={r.countryOfOrigin} onChange={(e) => upd(setRows, i, { countryOfOrigin: e.target.value })} className="ih-input-sm" /></td>
                        <td className="ih-review-cell"><Input type="number" value={r.declaredValue} onChange={(e) => upd(setRows, i, { declaredValue: Number(e.target.value) })} className="ih-input-sm ih-text-right" /></td>
                        <td className="ih-review-cell ih-text-xs ih-text-muted">{r.source}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {step === "done" && (
            <div className="ih-success-block">
              <Check size={48} className="ih-tone-success" />
              <div className="ih-font-semibold" style={{ fontSize: 16 }}>Entry packet processed</div>
              <p className="ih-text-sm ih-text-muted">
                {rows.length} products created · Entry {entryNumber} ({port})
              </p>
              <Button variant="primary" onClick={close}>Done</Button>
            </div>
          )}
        </DialogBody>
        {(step === "upload" || step === "review") && (
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={step === "upload" ? close : () => setStep("upload")}
              style={{ marginRight: "auto" }}
            >
              <ChevronLeft size={14} /> {step === "upload" ? "Cancel" : "Back"}
            </Button>
            <Button
              variant="primary"
              onClick={step === "upload" ? parse : confirm}
              disabled={step === "upload" ? files.length === 0 : rows.length === 0}
            >
              {step === "upload"
                ? "Parse packet"
                : `Confirm — create ${rows.length} products + entry`}{" "}
              <ChevronRight size={14} />
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

function upd(
  set: React.Dispatch<React.SetStateAction<ExtractedRow[]>>,
  i: number,
  patch: Partial<ExtractedRow>,
) {
  set((rows) => rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
}

const Field: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
}> = ({
  label,
  value,
  onChange,
}) => {
  return (
    <div className="ih-col ih-gap-1">
      <div className="ih-text-xs ih-text-muted">{label}</div>
      <Input value={value} onChange={(e) => onChange(e.target.value)} className="ih-input-sm" />
    </div>
  );
}
